export interface NavNode {
  label: string;
  to?: string;
  subItems?: NavNode[];
}

export interface ActiveNav {
  activeTo: string | null;
  trail: Set<string>;
}

export const menuItemKey = (parentKey: string, label: string) =>
  parentKey ? `${parentKey}>${label}` : label;

export const normalizePath = (path: string) => {
  const collapsed = path.replace(/\/{2,}/g, "/");
  return collapsed.length > 1 && collapsed.endsWith("/")
    ? collapsed.slice(0, -1)
    : collapsed;
};

/**
 * Length of `to` when it matches `pathname` exactly or as a parent segment, else -1.
 * Comparing lengths lets the deepest matching menu entry win, so routes hidden from
 * the menu (`/targets/imports/analyze`) still light up their nearest visible parent.
 */
export const pathMatchLength = (pathname: string, to: string) => {
  const current = normalizePath(pathname);
  const candidate = normalizePath(to);
  if (current === candidate) return candidate.length;
  if (candidate === "/") return -1;
  return current.startsWith(`${candidate}/`) ? candidate.length : -1;
};

export const findActiveNav = (items: NavNode[], pathname: string): ActiveNav => {
  let best = -1;
  let activeTo: string | null = null;
  let trail: string[] = [];

  const walk = (nodes: NavNode[], ancestors: string[], parentKey: string) => {
    for (const node of nodes) {
      const key = menuItemKey(parentKey, node.label);

      if (node.to) {
        const score = pathMatchLength(pathname, node.to);
        if (score > best) {
          best = score;
          activeTo = normalizePath(node.to);
          trail = node.subItems ? [...ancestors, key] : ancestors;
        }
      }

      if (node.subItems) walk(node.subItems, [...ancestors, key], key);
    }
  };

  walk(items, [], "");

  return { activeTo, trail: new Set(trail) };
};

export const isNavItemActive = (to: string | undefined, activeTo: string | null) =>
  !!to && activeTo !== null && normalizePath(to) === activeTo;
